import { useState, useEffect, useCallback, useRef } from 'react';
import * as moment from 'moment';
import { IOrderRequest } from '@nkr/models/index';
import { GridApi } from 'ag-grid-community';

export enum ShipVendorLogDateType {
    RequestDate = 1,
    LastTrackingUpdate = 2
}

interface IDateRange {
    startDate: string;
    endDate: string;
    dateType: number;
}

const matchesSearchCriteria = (order: IOrderRequest, searchCriteria: Partial<IOrderRequest>): boolean => {
    for (const [key, value] of Object.entries(searchCriteria)) {
        if (value === undefined || value === '') continue;

        switch (key) {
            case 'id':
            case 'entityType':
            case 'entityTypeId':
            case 'shippingVendorId':
            case 'statusId':
            case 'phlebotRequestId':
            case 'organType':
                if (order[key] !== Number(value)) return false;
                break;

            case 'entityTypeIds':
                if (Array.isArray(value) && !value.includes(order.entityType)) return false;
                break;
            case 'statusIds':
                if (Array.isArray(value) && !value.includes(order.statusId)) return false;
                break;

            case 'swapNumber':
            case 'swapUrl':
            case 'alias':
            case 'patientCenterAlias':
            case 'patientAlias':
            case 'vendorOrderNumber':
            case 'outboundTrackingNumber':
            case 'inboundTrackingToNkrLab':
            case 'internalTrackingNumber':
            case 'inboundTrackingNumber':
            case 'comments':
            case 'donorAlias':
            case 'donorCenterAlias':
                if (!order[key]?.toLowerCase().includes(value.toString().toLowerCase())) return false;
                break;

            case 'orderDateTime':
                const orderDate = moment(order.orderDateTime).format('YYYY-MM-DD');
                if (orderDate !== value) return false;
                break;

            case 'lastUpdated':
                const lastUpdated = moment(order.lastUpdated).format('YYYY-MM-DD');
                if (lastUpdated !== value) return false;
                break;

            case 'emailNoticeSent':
            case 'phlebotRequired':
                if (order[key] !== (value === 'true')) return false;
                break;

            default:
                console.warn(`Unhandled search criteria: ${key}`);
        }
    }
    return true;
};

const matchesDateRange = (order: IOrderRequest, dateRange: IDateRange): boolean => {
    var orderDate = moment(order.orderDateTime);

    if (dateRange.dateType == ShipVendorLogDateType.LastTrackingUpdate) {
        orderDate = moment(order.lastUpdated);
    }

    // endDate arrives as a bare 'YYYY-MM-DD' string, which moment parses as midnight -
    // compare against the end of that day so orders placed later that same day are included.
    const endOfEndDate = moment(dateRange.endDate).endOf('day');

    return orderDate.isSameOrAfter(dateRange.startDate) && orderDate.isSameOrBefore(endOfEndDate);
};

export const useOrderTrackingData = (
    list: IOrderRequest[],
    gridApi: GridApi | null,
    activeTab: 'todo' | 'all',
    onDateSearch?: (startDate: string, endDate: string, dateType: number) => void
) => {
    const [filteredList, setFilteredList] = useState<IOrderRequest[]>([]);
    const [todoList, setTodoList] = useState<IOrderRequest[]>([]);

    // Search criteria (kit type / status pills, alias text, the liver-only lock, etc.) and the
    // date range are two independent filters that both need to stay applied together - neither
    // one should be able to silently drop the other when it re-runs.
    const searchCriteriaRef = useRef<Partial<IOrderRequest>>({});
    const dateRangeRef = useRef<IDateRange | null>(null);

    const updateTodoList = (orders: IOrderRequest[]) => {
        const todoItems = orders.filter(order => ![5, 6].includes(order.statusId));
        setTodoList(todoItems);
    };

    const applyFilters = useCallback((sourceList: IOrderRequest[]) => {
        const dateRange = dateRangeRef.current;

        const newFilteredList = sourceList.filter(order =>
            matchesSearchCriteria(order, searchCriteriaRef.current)
            && (dateRange == null || matchesDateRange(order, dateRange))
        );

        setFilteredList(newFilteredList);
        updateTodoList(newFilteredList);
        if (gridApi) {
            gridApi.setRowData(activeTab === 'todo' ? todoList : newFilteredList);
        }
    }, [gridApi, activeTab, todoList]);

    useEffect(() => {
        applyFilters(list);
    }, [list]);

    const handleSearch = useCallback((searchCriteria: Partial<IOrderRequest>) => {
        searchCriteriaRef.current = searchCriteria;
        applyFilters(list);
    }, [list, applyFilters]);

    const handleDateRangeSearch = useCallback((startDate: string, endDate: string, dateType: number) => {
        dateRangeRef.current = { startDate, endDate, dateType };
        applyFilters(list);

        if (onDateSearch) {
            onDateSearch(startDate, endDate, dateType);
        }
    }, [list, applyFilters, onDateSearch]);

    return {
        filteredList,
        todoList,
        updateTodoList,
        handleSearch,
        handleDateRangeSearch
    };
};
